import { bindActionCreators } from 'redux';
import forEach from 'lodash/forEach';
import { me } from 'legacy-src/queries';
import {
  setIsReadOnly,
  setStudentId,
  setSectionId,
  setEducationPeriod,
  setSubject,
  setIsWelcomeVideoFirstTime,
  doImpersonate as doImpersonateAction,
  logoutImpersonate as logoutImpersonateAction,
  refreshImpersonatorAccessToken as refreshImpersonatorAccessTokenAction,
} from 'legacy-src/components/impersonating/state/actions';
import {
  saveLocalStorageData,
  isImpersonating,
  getImpersonatorTokens,
  updateImpersonatorAccessToken,
  getStudentTokens,
  getImpersonatedStudentId,
  emptyImpersonaterLocalStorage,
} from 'legacy-src/components/impersonating/utils';
import { pageReloading } from 'legacy-src/actions/commonActions';
import { login } from 'legacy-src/components/auth/actions';
import { getEducationPeriodForSubject } from 'legacy-src/components/auth/utils';
import { setItems } from 'legacy-src/actions/classes';
import postImpersonatingTelemetryData from '../utils/telemetry';
import {
  IMPERSONATING_TELEMETRY_TYPES,
  LC_CONSTANTS,
} from '../utils/constants';
import {
  unitsClient,
  fymClient,
  impersonatorFymClient,
  reportsClient,
  wizardsClient,
} from 'legacy-src/utility/GraphqlClients';

/**
 * @todo - check SSO teacher session (livity)
 * */

/**
 * Hook for doing the impersonating call
 * @param {function} dispatch The redux dispatch method
 * @param {object} router
 * @param {object} userData
 * @param {object} impersonating
 * @param {function} callback
 * */
export default ({
  dispatch,
  router,
  userData,
  impersonating,
  callback = () => {},
}) => {
  const sagaDispatch = bindActionCreators(
    {
      doImpersonateAction,
      logoutImpersonateAction,
      refreshImpersonatorAccessTokenAction,
      pageReloading,
      pageReloadingFulfill: pageReloading.fulfill,
      loginSuccess: login.success,
      setItemsAction: setItems,
    },
    dispatch,
  );

  const { teacherClasses = [] } = impersonating || {};

  /**
   * @param {string} sectionId
   * @param {string} studentId
   * @param {string} studentName
   * @param {string} sectionName
   * */
  const doImpersonate = async ({
    sectionId,
    studentId,
    studentName = '',
    sectionName = '',
  }) => {
    const impersonatedStudentId = getImpersonatedStudentId();
    /**
     * Do not impersonate the same student
     * */
    if (impersonatedStudentId === studentId) {
      return;
    }

    // if we are changing student then send a stop telementry for the old student
    if (impersonatedStudentId) {
      const stopImpersonatedTelData = {
        eventType: IMPERSONATING_TELEMETRY_TYPES.STOP,
        studentId: impersonatedStudentId,
        userData,
        subjectId: window.localStorage.getItem(LC_CONSTANTS.ISUBJECT),
        sectionId: window.localStorage.getItem(LC_CONSTANTS.SECTION_ID),
        teacherClasses,
      };
      postImpersonatingTelemetryData(stopImpersonatedTelData);
    }
    sagaDispatch.pageReloading();
    const { refreshToken, expires } = getImpersonatorTokens();

    sagaDispatch.refreshImpersonatorAccessTokenAction({
      headers: {
        skipOverride: true,
        skipRefresh: true,
        accessToken: refreshToken,
        refreshToken,
        expires,
      },
      onReady: (refreshResponse) => {
        const {
          access_token: newAccessToken,
          expires: newExpires,
          msg = '',
        } = refreshResponse;
        if (msg) {
          emptyImpersonaterLocalStorage();
          return;
        }
        updateImpersonatorAccessToken({
          accessToken: newAccessToken,
          expires: Date.parse(newExpires),
        });
        sagaDispatch.doImpersonateAction({
          headers: {
            skipOverride: true,
            accessToken: newAccessToken,
            refreshToken,
            expires: newExpires,
          },
          params: {
            id: studentId,
          },
          onReady: (response) => {
            sagaDispatch.setItemsAction([]);
            forEach(
              [
                unitsClient,
                fymClient,
                impersonatorFymClient,
                reportsClient,
                wizardsClient,
              ],
              async (client) => {
                client && client.resetStore && (await client.resetStore());
              },
            );

            const next = () => {
              const {
                subject,
                currentSubject,
                educationPeriod: currentEducationPeriod,
              } = impersonating;
              const { subject: routerSubject } = router.params;
              const educationPeriod =
                currentEducationPeriod ||
                getEducationPeriodForSubject({
                  subjectId: currentSubject,
                  userData,
                });
              saveLocalStorageData(
                response,
                sectionId,
                educationPeriod,
                currentSubject || routerSubject,
                studentName,
                sectionName,
              );
              setIsReadOnly(dispatch, true);
              setStudentId(dispatch, studentId);
              setSectionId(dispatch, sectionId);
              setEducationPeriod(dispatch, educationPeriod);
              if (!subject && routerSubject) {
                setSubject(dispatch, routerSubject);
              }
              setIsWelcomeVideoFirstTime(dispatch, true);

              // send start event for new impersonated student
              const startImpersonatedTelData = {
                eventType: IMPERSONATING_TELEMETRY_TYPES.START,
                studentId,
                sectionId,
                subjectId: currentSubject || routerSubject,
                userData,
                teacherClasses,
              };
              postImpersonatingTelemetryData(startImpersonatedTelData);

              fymClient
                .query({
                  query: me,
                  fetchPolicy: 'network-only',
                })
                .then(({ data }) => {
                  const {
                    currentEducationPeriod,
                    educationPeriods,
                    nextEducationPeriod,
                  } = data;
                  sagaDispatch.loginSuccess({
                    ...data,
                    me: {
                      ...data.me,
                      ssoLogin: 'Not SSO',
                    },
                    educationPeriod: {
                      currentEducationPeriod,
                      educationPeriods,
                      nextEducationPeriod,
                    },
                  });
                  callback();
                  router.replace(`/${currentSubject || routerSubject}/home`);
                  sagaDispatch.pageReloadingFulfill();
                });
            };

            if (isImpersonating()) {
              /**
               * @todo - answer this: What happens with the endpoints that would fire exactly after logout
               * (use case is you impersonate student assignment results page which takes time to load)
               * */
              const {
                accessToken: studentAccessToken,
                refreshToken: studentRefreshToken,
                expires: studentExpires,
              } = getStudentTokens();
              sagaDispatch.logoutImpersonateAction({
                headers: {
                  skipOverride: true,
                  accessToken: studentAccessToken,
                  refreshToken: studentRefreshToken,
                  expires: studentExpires,
                },
                onReady: () => next(),
              });
            } else {
              next();
            }
          },
        });
      },
    });
  };

  return [{}, doImpersonate];
};
